<template>
  <div class="company-page">
    <header>
      <div class="nav-row">
        <button @click="$router.go(-1)">‹</button>
        <span>关于我们</span>
        <i class="nav-spacer" aria-hidden="true"></i>
      </div>
      <span class="en">ASM PACIFIC WEALTH</span>
      <h1>定期返利 · 分红规划 · 权益保障</h1>
      <p>围绕明确周期、约定返利和到期结算，为客户提供清晰可查的产品服务。</p>
      <div class="license-note"><span>✓</span> 产品规则公开 · 合同自动生成 · 服务流程可追溯</div>
    </header>

    <main>
      <section class="intro">
        <span>ABOUT US</span>
        <h2>ASM太平洋财富管理平台</h2>
        <p>平台围绕定期返利、定投分红与到期结算产品，提供从产品展示、实名签约、订单管理到收益记录的一站式服务。每笔购买独立记录本金、周期、返利方式和到期日，并生成关联本人电子签名的本金及约定收益保障合同，方便用户随时核对合同权益。</p>
        <div class="stats">
          <div><strong>1975</strong><span>品牌创立</span></div>
          <div><strong>全球50+</strong><span>国家与地区</span></div>
          <div><strong>全流程</strong><span>风控管理</span></div>
        </div>
      </section>

      <section class="document-grid">
        <button v-for="item in documents" :key="item.title" @click="openDocument(item)">
          <span :class="item.tone">{{ item.icon }}</span>
          <div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
          <b>›</b>
        </button>
      </section>

      <section class="qualification">
        <div class="section-title">
          <div><span>COMPLIANCE</span><h2>公司资质与合规</h2></div>
          <small>信息展示</small>
        </div>
        <div class="qualification-card">
          <div class="qualification-logo"><img :src="qualificationLogo" alt="中国太平洋保险" /></div>
          <div>
            <h3>主体资质与承保文件</h3>
            <p>公司注册证明及产品承保公告函已公示，可点击查看完整文件。</p>
            <button @click="activeDocument = qualifications">查看资质文件 →</button>
          </div>
        </div>
      </section>

      <section class="guide-section">
        <div class="section-title">
          <div><span>GETTING STARTED</span><h2>新手引导</h2></div>
          <small>4 步开始</small>
        </div>
        <div class="steps">
          <article v-for="(step, index) in steps" :key="step.title">
            <span>0{{ index + 1 }}</span>
            <div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div>
            <button @click="$router.push(step.path)">去完成</button>
          </article>
        </div>
      </section>

      <section class="service-section">
        <div class="service-copy">
          <span>ONLINE SERVICE</span>
          <h2>需要帮助？联系在线客服</h2>
          <p>服务时间：周一至周日 09:00–21:00</p>
        </div>
        <button @click="$router.push('/customerService')">立即咨询</button>
      </section>

      <section class="announcement">
        <div class="section-title">
          <div><span>ANNOUNCEMENTS</span><h2>公司活动与公告</h2></div>
          <button @click="$router.push('/article')">全部 ›</button>
        </div>
        <article v-for="notice in notices" :key="notice.id" @click="openNotice(notice)">
          <div><span>{{ notice.month }}</span><strong>{{ notice.day }}</strong></div>
          <p><b>{{ notice.tag }}</b>{{ notice.title }}</p>
          <img :src="notice.image" :alt="notice.title" />
          <i>›</i>
        </article>
        <div v-if="noticesLoaded && !notices.length" class="announcement-empty">
          暂无最新公告，点击“全部”查看公告中心
        </div>
      </section>

      <p class="footer-copy">© 2026 ASM太平洋财富管理平台</p>
    </main>

    <div v-if="activeDocument" class="document-modal" @click.self="activeDocument = null">
      <article>
        <button @click="activeDocument = null">×</button>
        <span>{{ activeDocument.english }}</span>
        <h1>{{ activeDocument.title }}</h1>
        <div v-if="activeDocument.stats" class="document-stats">
          <section v-for="stat in activeDocument.stats" :key="stat.label">
            <strong>{{ stat.value }}</strong>
            <span>{{ stat.label }}</span>
          </section>
        </div>
        <p v-for="paragraph in activeDocument.content" :key="paragraph">{{ paragraph }}</p>
        <div v-if="activeDocument.images" class="qualification-gallery">
          <button
            v-for="item in activeDocument.images"
            :key="item.title"
            type="button"
            @click="openImagePreview(item)"
          >
            <img :src="item.thumb" :alt="item.title" loading="lazy" />
            <span><strong>{{ item.title }}</strong><small>点击查看原图</small></span>
          </button>
        </div>
        <div v-if="activeDocument.timeline" class="document-timeline">
          <section v-for="milestone in activeDocument.timeline" :key="milestone.year">
            <strong>{{ milestone.year }}</strong>
            <p>{{ milestone.text }}</p>
          </section>
        </div>
        <div v-if="activeDocument.points" class="document-points">
          <section v-for="point in activeDocument.points" :key="point.title">
            <h3>{{ point.title }}</h3>
            <p>{{ point.desc }}</p>
          </section>
        </div>
        <div v-if="activeDocument.sources" class="document-sources">
          <strong>官方资料来源</strong>
          <a
            v-for="source in activeDocument.sources"
            :key="source.url"
            :href="source.url"
            target="_blank"
            rel="noopener noreferrer"
          >{{ source.title }} ↗</a>
        </div>
      </article>
    </div>
    <div v-if="previewAsset" class="qualification-preview" @click.self="previewAsset = null">
      <button type="button" aria-label="关闭" @click="previewAsset = null">×</button>
      <div>
        <img :src="previewAsset.src" :alt="previewAsset.title" />
        <p>{{ previewAsset.title }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@utils/request";
import companyOverviewImage from "../../assets/images/company-cards/company-overview.jpg";
import corporateCultureImage from "../../assets/images/company-cards/corporate-culture.jpg";
import businessOverviewImage from "../../assets/images/company-cards/project-introduction.jpg";
import qualificationLogo from "../../assets/images/qualifications/pacific-insurance-logo.svg";
import caymanIncorporation from "../../assets/images/qualifications/cayman-incorporation.webp";
import caymanIncorporationThumb from "../../assets/images/qualifications/cayman-incorporation-thumb.webp";
import hongKongIncorporation from "../../assets/images/qualifications/hong-kong-incorporation.webp";
import hongKongIncorporationThumb from "../../assets/images/qualifications/hong-kong-incorporation-thumb.webp";
import businessRegistration from "../../assets/images/qualifications/hong-kong-business-registration.webp";
import businessRegistrationThumb from "../../assets/images/qualifications/hong-kong-business-registration-thumb.webp";
import hongKongListing from "../../assets/images/qualifications/hong-kong-listing.webp";
import hongKongListingThumb from "../../assets/images/qualifications/hong-kong-listing-thumb.webp";
import underwritingAnnouncement from "../../assets/images/qualifications/underwriting-announcement.webp";
import underwritingAnnouncementThumb from "../../assets/images/qualifications/underwriting-announcement-thumb.webp";

export default {
  name: "CompanyHub",
  data() {
    return {
      activeDocument: null,
      previewAsset: null,
      qualificationLogo,
      qualifications: {
        title: "公司资质与合规说明", english: "QUALIFICATIONS & COMPLIANCE",
        content: [
          "以下为公司主体登记及产品承保相关文件。点击文件可查看高清原图，具体内容以公示文件原件为准。"
        ],
        images: [
          { title: "开曼群岛公司注册证书", src: caymanIncorporation, thumb: caymanIncorporationThumb },
          { title: "香港公司注册证明书", src: hongKongIncorporation, thumb: hongKongIncorporationThumb },
          { title: "香港商业登记证", src: businessRegistration, thumb: businessRegistrationThumb },
          { title: "香港交易所上市证明", src: hongKongListing, thumb: hongKongListingThumb },
          { title: "产品承保官方公告函", src: underwritingAnnouncement, thumb: underwritingAnnouncementThumb }
        ]
      },
      documents: [
        {
          title: "公司白皮书", desc: "发展历程、业务版图与创新能力", icon: "书", tone: "blue",
          english: "COMPANY WHITE PAPER",
          stats: [
            { value: "1975", label: "公司创立" },
            { value: "50年", label: "产业积累" },
            { value: "30+", label: "直接覆盖国家" },
            { value: "15", label: "全球研发中心" }
          ],
          content: [
            "ASMPT 是面向全球半导体与电子制造行业的硬件、软件和服务解决方案供应商，愿景为“Enabling the digital world（赋能数字世界）”。公司业务贯穿芯片互连载体、芯片组装与封装、表面贴装技术及智能工厂软件，是少数能够覆盖电子制造主要环节的综合解决方案企业。",
            "公司创立于1975年，1989年在香港联合交易所上市。2002年成为全球领先的半导体组装与封装设备制造商；2011年收购西门子 SIPLACE 业务并将全球总部设于新加坡；2013年收购英国 DEK，进一步形成覆盖印刷、贴装和智能制造的软件硬件组合。",
            "根据 ASMPT 2025 年官方 ESG 报告，公司在全球拥有约9,000名员工、10,000多家客户、超过300万套已安装系统、15个研发中心及1,700多项关键前沿技术专利，并在30多个国家直接开展业务。",
            "集团目前形成两大核心业务板块：半导体解决方案（SEMI）与表面贴装技术解决方案（SMT），同时由全资子公司 Critical Manufacturing 提供制造执行系统（MES），推动设备、生产线、工厂与多工厂层面的连接、自动化和优化。",
            "面向人工智能、高性能计算、汽车电子、光电、通信和工业电子等市场，ASMPT 持续投入先进封装、热压键合、混合键合、激光切割、精密贴装和智能工厂技术研发，以提升电子制造的效率、可靠性和可持续性。"
          ],
          timeline: [
            { year: "1975", text: "作为 ASM International 的亚洲市场业务创立。" },
            { year: "1989", text: "正式在香港联合交易所上市。" },
            { year: "2002", text: "成为全球领先的半导体组装与封装设备制造商。" },
            { year: "2011", text: "收购 SIPLACE 业务，新加坡成为全球总部。" },
            { year: "2013", text: "收购 DEK，强化 SMT 印刷及智能制造能力。" },
            { year: "2022", text: "收购激光切割与开槽技术企业 ALSI。" },
            { year: "2025", text: "迎来创立50周年，持续推进智能与可持续制造。" }
          ],
          points: [
            { title: "SEMI 半导体解决方案", desc: "覆盖先进封装与主流封装，包括固晶、焊线、热压键合、倒装、塑封、激光切割、检测、测试及包装等关键工序。" },
            { title: "SMT 表面贴装解决方案", desc: "以 DEK 印刷平台、SIPLACE 贴装系统及智能工厂工具为核心，服务从多品种小批量到大规模生产的电子制造场景。" },
            { title: "智能制造软件", desc: "Critical Manufacturing 提供现代化 MES；WORKS 软件套件支持设备连接、生产分析、物料管理与工厂级优化。" },
            { title: "创新与研发", desc: "通过遍布亚洲、欧洲和美洲的研发网络，持续推进 Chiplet、先进封装、光子与高精度自动化技术。" },
            { title: "全球客户与应用", desc: "解决方案服务于计算与服务器、智能汽车、通信、消费电子、工业设备、医疗技术、LED及光电等行业。" },
            { title: "可持续发展", desc: "ESG 融入集团战略，官方目标包括在2035年前实现范围1和范围2净零排放，并推进经 SBTi 验证的减排目标。" }
          ],
          sources: [
            { title: "ASMPT 公司概览", url: "https://www.asmpt.com/en/company/" },
            { title: "ASMPT 发展历程", url: "https://www.asmpt.com/en/company/history/" },
            { title: "ASMPT 集团架构", url: "https://www.asmpt.com/en/company/group-structure/" },
            { title: "ASMPT 2025 ESG 报告", url: "https://www.asmpt.com/site/assets/files/84853/asmpt_esg_report_2025_en.pdf" }
          ]
        },
        {
          title: "业务简介", desc: "平台定位与核心能力", icon: "业", tone: "purple",
          english: "BUSINESS OVERVIEW",
          content: [
            "全球半导体封装设备龙头ASM太平洋正式与观势俱乐部建立战略协同。",
            "受益AI算力浪潮，HBM、先进封装设备需求迎来持续增长周期，ASM太平洋持续加码TCB设备、Chiplet封装技术研发与产能扩张。",
            "依托本次合作，观势俱乐部会员专属通道正式开放，参与ASM太平洋先进封装产业链项目，共享半导体产业发展红利。"
          ],
          points: [
            { title: "✅资金投向：", desc: "HBM产线扩建、光模块封装设备研发、国内封测配套项目" },
            { title: "✅配套权益：", desc: "产业研报定期推送、企业实地调研名额、后续新项目优先认购权。算力时代，布局半导体核心赛道，与行业龙头共同成长！" }
          ]
        },
        {
          title: "服务与风控", desc: "产品准入与风险管理", icon: "盾", tone: "green",
          english: "SERVICE & RISK CONTROL",
          content: ["平台建立产品资料准入、合作机构核验、订单合同生成、电子签名存证、返利结算复核和异常订单处理机制。", "系统按订单保存购买金额、产品周期、约定比例、返利方式、签署时间及保障期限；关键操作记录可追溯，并通过账户明细向用户展示。"],
          points: [
            { title: "产品准入", desc: "核验项目资料、结算规则、合作机构和保障文件。" },
            { title: "合同存证", desc: "购买时固化订单要素与本人电子签名，形成独立合同编号。" },
            { title: "结算复核", desc: "按约定返利方式执行日常返息与到期本息结算。" }
          ]
        },
        {
          title: "隐私与安全", desc: "账户及数据保护说明", icon: "安", tone: "gold",
          english: "PRIVACY & SECURITY",
          content: ["平台遵循最小必要原则收集和使用个人信息，并通过访问控制、传输保护和操作留痕降低数据风险。", "请妥善保管登录密码、支付密码和验证码，不向任何人透露。客服人员不会索取您的完整密码。"]
        }
      ],
      steps: [
        { title: "注册并完成登录", desc: "使用本人信息注册账户并妥善设置密码。", path: "/register" },
        { title: "完成身份认证", desc: "提交适当性管理所需的真实身份资料。", path: "/memmber_business" },
        { title: "了解产品与返利方式", desc: "查看产品周期、返利比例、结算方式与到期安排。", path: "/category" },
        { title: "查看合同保障权益", desc: "购买后在每笔产品中查看本金及约定收益保障电子合同。", path: "/myInvest" }
      ],
      notices: [],
      noticesLoaded: false
    };
  },
  mounted() {
    this.loadNotices();
  },
  methods: {
    openImagePreview(item) {
      this.previewAsset = item;
    },
    openDocument(item) {
      this.activeDocument = item;
    },
    openNotice(notice) {
      if (!notice || !notice.id) {
        this.$router.push("/article");
        return;
      }
      this.$router.push({
        name: "article_detail",
        query: { id: notice.id }
      });
    },
    formatNoticeDate(value) {
      let date;
      if (/^\d{10}$/.test(String(value || ""))) {
        date = new Date(Number(value) * 1000);
      } else {
        date = new Date(String(value || "").replace(/-/g, "/"));
      }
      if (Number.isNaN(date.getTime())) {
        date = new Date();
      }
      const months = [
        "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
        "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
      ];
      return {
        month: months[date.getMonth()],
        day: String(date.getDate()).padStart(2, "0")
      };
    },
    normalizeNoticeTitle(value) {
      const title = String(value || "").trim();
      return title === "项目简介" ? "业务简介" : title;
    },
    noticeImage(title, index) {
      const normalized = this.normalizeNoticeTitle(title);
      const images = {
        公司简介: companyOverviewImage,
        企业文化: corporateCultureImage,
        业务简介: businessOverviewImage
      };
      const fallbacks = [
        companyOverviewImage,
        corporateCultureImage,
        businessOverviewImage
      ];
      return images[normalized] || fallbacks[index % fallbacks.length];
    },
    loadNotices() {
      request
        .post("/api/v1/article/cat/all", {}, { login: false })
        .then(res => {
          const groups = (res && res.data && res.data.data) || [];
          const articles = groups.reduce(
            (list, group) => list.concat((group && group.list) || []),
            []
          );
          this.notices = articles
            .filter(item => item && item.id && item.title)
            .slice(0, 3)
            .map((item, index) => {
              const date = this.formatNoticeDate(item.add_time);
              const title = this.normalizeNoticeTitle(item.title);
              return {
                id: item.id,
                month: date.month,
                day: date.day,
                tag: item.cate_name || "公司公告",
                title,
                image: this.noticeImage(title, index)
              };
            });
        })
        .catch(() => {
          this.notices = [];
        })
        .finally(() => {
          this.noticesLoaded = true;
        });
    }
  }
};
</script>

<style scoped lang="less">
.company-page { width: 100%; max-width: 100%; min-height: 100vh; overflow-x: hidden; color: #1e2c42; background: #f4f7fa; }
.company-page,.company-page * { box-sizing: border-box; }
button { font: inherit; border: 0; background: none; }
header {
  position: relative;
  padding: 15px 18px 28px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(90deg,rgba(4,21,49,.96) 0%,rgba(6,34,72,.87) 50%,rgba(7,42,82,.52) 100%),
    url("../../assets/images/asm-premium/global-headquarters.webp") 68% center / cover no-repeat;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 18px 42px rgba(6,31,66,.18);
}
header::after {
  position: absolute;
  right: 18px;
  bottom: 17px;
  width: 154px;
  height: 28px;
  content: "";
  opacity: .17;
  background: url("../../assets/images/asmpt_logo.svg") right center / contain no-repeat;
  filter: brightness(0) invert(1);
  pointer-events: none;
}
.nav-row,header > .en,header > h1,header > p,.license-note { position: relative; z-index: 1; }
.nav-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; font-size: 14px; }
.nav-row button { width: 45px; color: #fff; font-size: 10px; }
.nav-row button:first-child { text-align: left; font-size: 27px; }
.nav-spacer { display: block; width: 45px; height: 1px; }
.en,.intro > span,.section-title span,.service-copy > span,.document-modal article > span { color: #83b6e3; font-size: 8px; font-weight: 700; letter-spacing: 1.4px; }
header h1 { margin: 8px 0; font-size: 25px; }
header > p { max-width: 330px; color: rgba(255,255,255,.67); font-size: 10px; line-height: 1.7; }
.license-note { margin-top: 20px; padding: 11px; color: #dcecff; font-size: 9px; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.11); border-radius: 11px; }
.license-note span { display: inline-grid; width: 17px; height: 17px; margin-right: 7px; place-items: center; color: #143a67; background: #8bd7bd; border-radius: 50%; }
main { padding: 14px; }
.intro,.qualification,.guide-section,.announcement { margin-bottom: 14px; padding: 18px 15px; background: #fff; border-radius: 19px; }
.intro h2 { margin: 6px 0 10px; font-size: 19px; }
.intro > p { color: #718095; font-size: 10px; line-height: 1.8; }
.stats { display: grid; grid-template-columns: repeat(3,1fr); margin-top: 17px; padding-top: 15px; border-top: 1px solid #edf0f4; }
.stats div { text-align: center; border-left: 1px solid #edf0f4; }
.stats div:first-child { border: 0; }
.stats strong,.stats span { display: block; }
.stats strong { color: #285f9d; font-size: 15px; }
.stats span { margin-top: 4px; color: #929ca9; font-size: 8px; }
.document-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; margin-bottom: 14px; }
.document-grid button { position: relative; display: grid; grid-template-columns: 40px minmax(0,1fr); gap: 9px; align-items: center; padding: 14px 10px; overflow: hidden; text-align: left; background: #fff; border: 1px solid rgba(43,101,157,.08); border-radius: 16px; box-shadow: 0 8px 20px rgba(26,66,106,.045); }
.document-grid button::before { position: absolute; z-index: 0; inset: 0; content: ""; opacity: .16; background-position: right center; background-size: cover; pointer-events: none; }
.document-grid button:nth-child(1)::before { background-image: linear-gradient(90deg,#fff 42%,rgba(255,255,255,.55)),url("../../assets/images/asm-premium/chiplet-wafer.webp"); }
.document-grid button:nth-child(2)::before { background-image: linear-gradient(90deg,#fff 42%,rgba(255,255,255,.58)),url("../../assets/images/asm-premium/advanced-packaging-fab.webp"); }
.document-grid button:nth-child(3)::before { background-image: linear-gradient(90deg,#fff 42%,rgba(255,255,255,.58)),url("../../assets/images/asm-premium/global-rd-campus.webp"); }
.document-grid button:nth-child(4)::before { background-image: linear-gradient(90deg,#fff 42%,rgba(255,255,255,.55)),url("../../assets/images/asm-premium/global-headquarters.webp"); }
.document-grid button > * { position: relative; z-index: 1; }
.document-grid button > span { display: grid; width: 38px; height: 38px; place-items: center; font-size: 12px; font-weight: 700; border-radius: 12px; }
.document-grid .blue { color: #286dbd; background: #eaf3ff; }
.document-grid .purple { color: #6f52b1; background: #f0ebfb; }
.document-grid .green { color: #218267; background: #e7f7f1; }
.document-grid .gold { color: #a57327; background: #fff3dd; }
.document-grid h3 { font-size: 11px; }
.document-grid p { margin-top: 4px; color: #919ba8; font-size: 8px; }
.document-grid b { position: absolute; top: 9px; right: 9px; color: #b6bdc7; font-size: 14px; }
.section-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.section-title h2 { margin-top: 4px; font-size: 17px; }
.section-title small,.section-title button { color: #909aa7; font-size: 8px; }
.qualification-card {
  display: grid;
  grid-template-columns: 58px 1fr;
  gap: 13px;
  padding: 14px;
  background:
    linear-gradient(90deg,rgba(241,247,253,.98) 0%,rgba(249,252,255,.94) 62%,rgba(249,252,255,.8) 100%),
    url("../../assets/images/asm-premium/chiplet-wafer.webp") right center / 62% auto no-repeat;
  border: 1px solid #dbe7f3;
  border-radius: 14px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.8);
}
.qualification-logo { display: grid; width: 58px; height: 58px; padding: 7px; place-items: center; overflow: hidden; background: rgba(255,255,255,.9); border: 1px solid #d5e5f3; border-radius: 13px; box-shadow: 0 5px 14px rgba(25,91,145,.08); }
.qualification-logo img { display: block; width: 100%; height: 100%; object-fit: contain; }
.qualification-card h3 { font-size: 12px; }
.qualification-card p { margin: 6px 0; color: #7c8999; font-size: 9px; line-height: 1.55; }
.qualification-card button { color: #346fae; font-size: 8px; }
.qualification-tip { margin-top: 10px; color: #a18776; font-size: 8px; line-height: 1.5; }
.guide-section {
  background:
    linear-gradient(90deg,rgba(255,255,255,.985),rgba(255,255,255,.93)),
    url("../../assets/images/asm-premium/global-rd-campus.webp") center / cover no-repeat;
}
.steps article { display: grid; grid-template-columns: 35px 1fr 45px; gap: 9px; align-items: center; padding: 13px 7px; border-top: 1px solid #e9eef4; border-radius: 9px; }
.steps article:first-child { border-top: 0; }
.steps article > span { color: #b9cce3; font-size: 18px; font-weight: 700; }
.steps h3 { font-size: 11px; }
.steps p { margin-top: 4px; color: #909aa8; font-size: 8px; }
.steps button { color: #3472ba; font-size: 8px; }
.service-section {
  position: relative;
  display: flex;
  min-height: 94px;
  align-items: center;
  margin-bottom: 14px;
  padding: 19px 16px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(90deg,rgba(7,37,75,.95),rgba(16,70,116,.67)),
    url("../../assets/images/asm-premium/global-headquarters.webp") center / cover no-repeat;
  border-radius: 19px;
  box-shadow: 0 12px 30px rgba(17,65,108,.15);
}
.service-section::after { position: absolute; right: 16px; bottom: 10px; width: 110px; height: 20px; content: ""; opacity: .13; background: url("../../assets/images/asmpt_logo.svg") center / contain no-repeat; filter: brightness(0) invert(1); pointer-events: none; }
.service-section > * { position: relative; z-index: 1; }
.service-copy h2 { margin: 6px 0; font-size: 16px; }
.service-copy p { color: rgba(255,255,255,.63); font-size: 8px; }
.service-section > button { margin-left: auto; padding: 9px 12px; color: #2b5c80; font-size: 9px; font-weight: 700; background: #fff; border-radius: 16px; }
.announcement article {
  display: grid;
  grid-template-columns: 39px minmax(0,1fr) 76px 12px;
  gap: 10px;
  align-items: center;
  margin-top: 3px;
  padding: 12px 8px;
  background: linear-gradient(90deg,#fff,#f7fafc);
  border-top: 1px solid #e9eef4;
  border-radius: 10px;
}
.announcement article > div { text-align: center; }
.announcement article span,.announcement article strong { display: block; }
.announcement article span { color: #7993ad; font-size: 7px; }
.announcement article strong { color: #2b527e; font-size: 17px; }
.announcement article p { overflow: hidden; color: #4c5a6e; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.announcement article p b { margin-right: 6px; color: #3975b7; font-size: 8px; }
.announcement article > img { width: 76px; height: 52px; object-fit: cover; border-radius: 8px; box-shadow: 0 4px 12px rgba(28,70,109,.1); }
.announcement article i { color: #b4bcc6; font-style: normal; }
.announcement-empty {
  padding: 22px 10px 8px;
  color: #8a96a6;
  font-size: 11px;
  text-align: center;
}
.footer-copy { padding: 4px 0 12px; color: #a5aeb9; font-size: 8px; text-align: center; }
.document-modal { position: fixed; z-index: 100; inset: 0; display: flex; align-items: flex-end; background: rgba(5,15,31,.58); }
.document-modal > article { position: relative; width: 100%; max-height: 86vh; padding: 24px 19px 30px; overflow-y: auto; background: #fff; border-radius: 24px 24px 0 0; }
.document-modal article > button { position: absolute; top: 12px; right: 16px; color: #8d97a4; font-size: 26px; }
.document-modal h1 { margin: 8px 30px 15px 0; font-size: 21px; }
.document-modal article > p { margin-bottom: 13px; color: #637185; font-size: 10px; line-height: 1.85; }
.document-modal article > div { display: grid; gap: 9px; margin-top: 16px; }
.document-modal section { padding: 13px; background: #f5f8fc; border-radius: 12px; }
.document-modal section h3 { color: #315f95; font-size: 11px; }
.document-modal section p { margin-top: 5px; color: #7b8797; font-size: 9px; line-height: 1.6; }
.document-modal .document-stats {
  grid-template-columns: repeat(4,minmax(0,1fr));
  gap: 6px;
  margin: 0 0 18px;
}
.document-modal .document-stats section {
  padding: 11px 4px;
  text-align: center;
  background: #edf4fb;
}
.document-stats strong,.document-stats span { display: block; }
.document-stats strong { color: #225c98; font-size: 15px; }
.document-stats span { margin-top: 4px; color: #7a8999; font-size: 7px; }
.document-modal .document-timeline {
  position: relative;
  gap: 0;
  padding: 4px 0 3px 16px;
  border-left: 2px solid #d8e7f5;
}
.document-modal .document-timeline section {
  position: relative;
  display: grid;
  grid-template-columns: 43px 1fr;
  gap: 7px;
  align-items: start;
  padding: 8px 10px;
  background: transparent;
}
.document-timeline section::before {
  position: absolute;
  top: 10px;
  left: -22px;
  width: 10px;
  height: 10px;
  content: "";
  background: #2f6fad;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #bcd3e8;
}
.document-timeline section strong { color: #2f6fad; font-size: 10px; }
.document-modal .document-timeline section p { margin: 0; color: #647386; font-size: 9px; line-height: 1.6; }
.document-modal .document-points { grid-template-columns: repeat(2,minmax(0,1fr)); }
.document-modal .document-sources {
  display: flex;
  padding: 13px;
  flex-direction: column;
  align-items: flex-start;
  background: #f5f8fc;
  border-radius: 12px;
}
.document-sources > strong { color: #35485e; font-size: 10px; }
.document-sources a { color: #2e6dad; font-size: 9px; line-height: 1.5; }
.document-modal .qualification-gallery { grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; margin-top: 18px; }
.qualification-gallery > button { min-width: 0; overflow: hidden; text-align: left; background: #f5f8fc; border: 1px solid #dfe8f1; border-radius: 12px; box-shadow: 0 6px 18px rgba(24,62,99,.06); }
.qualification-gallery > button:last-child:nth-child(odd) { grid-column: 1 / -1; }
.qualification-gallery img { display: block; width: 100%; height: 180px; object-fit: contain; background: #eef2f6; }
.qualification-gallery span { display: flex; padding: 10px; flex-direction: column; gap: 3px; }
.qualification-gallery strong { color: #273a50; font-size: 10px; line-height: 1.45; }
.qualification-gallery small { color: #6f91b3; font-size: 8px; }
.qualification-preview { position: fixed; z-index: 180; inset: 0; display: flex; padding: 54px 12px 20px; align-items: flex-start; justify-content: center; overflow: auto; background: rgba(3,10,19,.93); }
.qualification-preview > button { position: fixed; z-index: 2; top: 12px; right: 16px; width: 36px; height: 36px; color: #fff; font-size: 30px; line-height: 32px; background: rgba(255,255,255,.12); border-radius: 50%; }
.qualification-preview > div { width: 100%; max-width: 720px; text-align: center; }
.qualification-preview img { display: block; width: 100%; height: auto; max-height: none; object-fit: contain; background: #fff; border-radius: 8px; }
.qualification-preview p { margin-top: 12px; color: #fff; font-size: 12px; }
</style>
